import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";

const SchedulingStep = ({
    onSchedule,
    campaignName,
    onCampaignNameChange,
    priority,
    onPriorityChange,
}) => {
    const [open, setOpen] = useState(false);
    const [selectedDateTime, setSelectedDateTime] = useState(dayjs());
    const [scheduleType, setScheduleType] = useState("now"); // "now" or "later"
    const notify = useNotification();

    // Generate 10 priority chips with decreasing opacity
    const priorityChips = Array.from({ length: 10 }, (_, index) => {
        const priorityNumber = index + 1;
        const opacity = 1 - index * 0.09;
        const backgroundColor = `rgba(0, 128, 128, ${opacity})`;
        return {
            priorityNumber,
            opacity,
            backgroundColor,
            label: `Priority ${priorityNumber}`,
        };
    });

    const handleScheduleNow = () => {
        const now = dayjs();
        setScheduleType("now");
        onSchedule({
            scheduleDateTime: now.format("YYYY-MM-DD HH:mm:ss"),
            scheduleType: "now",
        });
        notify("Messages will be sent immediately", "info");
    };

    const handleScheduleLater = () => {
        setOpen(true);
    };

    const handleConfirmSchedule = () => {
        if (selectedDateTime.isBefore(dayjs())) {
            notify("Please select a future date and time", "error");
            return;
        }
        setScheduleType("later");
        onSchedule({
            scheduleDateTime: selectedDateTime.format("YYYY-MM-DD HH:mm:ss"),
            scheduleType: "later",
        });
        setOpen(false);
        notify(
            `Messages scheduled for ${selectedDateTime.format("YYYY-MM-DD HH:mm")}`,
            "success"
        );
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                Schedule Campaign Messages
            </Typography>


            {/* Campaign Name */}
            <TextField
                fullWidth
                label="Campaign Name (Unique)"
                value={campaignName}
                onChange={(e) => onCampaignNameChange(e.target.value)}
                sx={{ mb: 3 }}
                required
                helperText="Enter a unique name for this campaign"
                error={!campaignName || campaignName.trim() === ""}
            />

            {/* Priority Selection */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Message Priority (1 = Highest, 10 = Lowest)
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    {priorityChips.map((chip) => (
                        <Chip
                            key={chip.priorityNumber}
                            label={chip.label}
                            onClick={() =>
                                onPriorityChange(chip.priorityNumber)
                            }
                            sx={{
                                backgroundColor:
                                    priority === chip.priorityNumber
                                        ? chip.backgroundColor
                                        : "default",
                                color:
                                    priority === chip.priorityNumber
                                        ? "white"
                                        : "default",
                                cursor: "pointer",
                            }}
                        />
                    ))}
                </Box>
                {priority && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Selected Priority: {priority}
                    </Typography>
                )}
            </Box>

            {/* Schedule Options */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    variant="contained"
                    onClick={handleScheduleNow}
                    size="small"
                    sx={WB_SX.primaryButton}
                >
                    Schedule Now
                </Button>
                <Button
                    variant="contained"
                    onClick={handleScheduleLater}
                    size="small"
                    sx={WB_SX.primaryButton}
                >
                    Schedule for Later
                </Button>
            </Box>

            {scheduleType && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {scheduleType === "now"
                        ? "Messages will be sent immediately when campaign is created."
                        : `Messages scheduled for ${selectedDateTime.format("YYYY-MM-DD HH:mm")}`}
                </Alert>
            )}

            {/* Schedule Later Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Schedule for Later</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Select Date & Time"
                            value={selectedDateTime}
                            onChange={(newValue) => setSelectedDateTime(newValue)}
                            minDateTime={dayjs()}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSchedule}
                        variant="contained"
                        sx={WB_SX.primaryButton}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default SchedulingStep;

